import type { AnimationEvent, MouseEvent, ReactNode } from 'react';
import { css, keyframes } from 'styled-components';
import styled from 'styled-components';

import { zIndexMap } from '../../../shared/config';
import { TIPS_SHARE_SHEET_CLOSE_ANIMATION_MS } from '../model';

type TipsShareSheetContainerProps = {
  isOpen: boolean;
  onCloseComplete?: () => void;
  children: ReactNode;
};

const sheetSlideIn = keyframes`
  from {
    transform: translateY(100%);
  }

  to {
    transform: translateY(0);
  }
`;

const sheetSlideOut = keyframes`
  from {
    transform: translateY(0);
  }

  to {
    transform: translateY(100%);
  }
`;

const getSheetAnimationCss = ({
  isOpen,
  closeAnimationMs,
}: {
  isOpen: boolean;
  closeAnimationMs: number;
}) =>
  isOpen
    ? css`
        animation: ${sheetSlideIn} 360ms cubic-bezier(0.22, 0.61, 0.36, 1) both;
      `
    : css`
        animation: ${sheetSlideOut} ${closeAnimationMs}ms ease-in both;
      `;

const BottomSheet = styled.div.attrs({
  'data-ui': 'tips-share-sheet-container',
})<{ $isOpen: boolean }>`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border-top-left-radius: ${({ theme }) => theme.scale.px(28)};
  border-top-right-radius: ${({ theme }) => theme.scale.px(28)};
  background: ${({ theme }) => theme.colors.surface.base};
  overflow: hidden;
  z-index: ${zIndexMap.tipsShare.sheet};
  will-change: transform;
  ${({ $isOpen }) =>
    getSheetAnimationCss({
      isOpen: $isOpen,
      closeAnimationMs: TIPS_SHARE_SHEET_CLOSE_ANIMATION_MS,
    })};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const SheetHandle = styled.div.attrs({
  'data-ui': 'tips-share-sheet-handle',
  'aria-hidden': true,
})`
  align-self: center;
  width: ${({ theme }) => theme.scale.px(56)};
  height: ${({ theme }) => theme.scale.px(4)};
  border-radius: ${({ theme }) => theme.scale.px(999)};
  margin-top: ${({ theme }) => theme.scale.px(11)};
  background: ${({ theme }) => theme.colors.text.primary};
  opacity: 0.35;
`;

const ContentSlot = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

export function TipsShareSheetContainer({
  isOpen,
  onCloseComplete,
  children,
}: TipsShareSheetContainerProps) {
  const stopBackdropClick = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  const handleAnimationEnd = (event: AnimationEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget || isOpen) {
      return;
    }

    onCloseComplete?.();
  };

  return (
    <BottomSheet
      $isOpen={isOpen}
      onAnimationEnd={handleAnimationEnd}
      onClick={stopBackdropClick}
    >
      <SheetHandle />
      <ContentSlot>{children}</ContentSlot>
    </BottomSheet>
  );
}
